
import React from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface MetricProgressBarProps {
  min: number;
  max: number;
  current: number;
  animatedProgress: number;
  isComplete: boolean;
  progressColor?: string;
  showZeroProgress?: boolean;
  hideMinLabel?: boolean;
  rightLabel?: string;
  className?: string;
  hideRightLabel?: boolean;
}

const MetricProgressBar: React.FC<MetricProgressBarProps> = ({
  min,
  max,
  current,
  animatedProgress,
  isComplete,
  progressColor,
  showZeroProgress = false,
  hideMinLabel = false,
  rightLabel,
  className,
  hideRightLabel = false
}) => {
  // Get the appropriate progress color based on completion state and custom color
  const getProgressColor = () => {
    if (progressColor) {
      return progressColor;
    }
    
    if (isComplete) {
      return '#00d023'; // Solid green for 100% completion
    } 
    
    // Default gradient that becomes more opaque as progress increases
    return `linear-gradient(90deg, #00d023, rgba(0, 208, 35, ${0.3 + (current / max * 100) * 0.007}))`;
  };

  return (
    <>
      <div className="flex justify-between text-xs text-gray-500 mb-1 h-4">
        <span>{hideMinLabel ? "" : min}</span>
        {!hideRightLabel && (
          <div className="flex items-center">
            <span>{rightLabel !== undefined && rightLabel !== '' ? rightLabel : max.toLocaleString()}</span>
            {isComplete && (
              <Check className="h-3 w-3 text-green-600 ml-1" strokeWidth={3} />
            )}
          </div>
        )}
      </div>
      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden relative">
        {showZeroProgress ? (
          <div 
            className={cn(
              "h-full rounded-full w-full pulse-red-effect",
              className
            )} 
            style={{
              background: progressColor
            }} 
          />
        ) : (
          <div 
            className={cn(
              "h-full rounded-full transition-all duration-1000",
              isComplete ? "rainbow-pulse-effect" : "",
              className
            )} 
            style={{
              width: `${animatedProgress}%`,
              background: getProgressColor()
            }} 
          />
        )}
      </div>
    </>
  );
};

export default MetricProgressBar;
